import { getNode, getPreviousPath, setNodes, unsetNodes, createPluginFactory, findNode } from '@udecode/plate-core';
import React from 'react';
import { KEY_INDENT, setIndent } from '@udecode/plate-indent';
import { Path } from 'slate';

function toVal(mix) {
	var k, y, str='';

	if (typeof mix === 'string' || typeof mix === 'number') {
		str += mix;
	} else if (typeof mix === 'object') {
		if (Array.isArray(mix)) {
			for (k=0; k < mix.length; k++) {
				if (mix[k]) {
					if (y = toVal(mix[k])) {
						str && (str += ' ');
						str += y;
					}
				}
			}
		} else {
			for (k in mix) {
				if (mix[k]) {
					str && (str += ' ');
					str += k;
				}
			}
		}
	}

	return str;
}

function clsx () {
	var i=0, tmp, x, str='';
	while (i < arguments.length) {
		if (tmp = arguments[i++]) {
			if (x = toVal(tmp)) {
				str && (str += ' ');
				str += x;
			}
		}
	}
	return str;
}

let ListStyleType;

(function (ListStyleType) {
  ListStyleType["Armenian"] = "armenian";
  ListStyleType["Circle"] = "circle";
  ListStyleType["CjkIdeographic"] = "cjk-ideographic";
  ListStyleType["Decimal"] = "decimal";
  ListStyleType["DecimalLeadingZero"] = "decimal-leading-zero";
  ListStyleType["Disc"] = "disc";
  ListStyleType["Georgian"] = "georgian";
  ListStyleType["Hebrew"] = "hebrew";
  ListStyleType["Hiragana"] = "hiragana";
  ListStyleType["HiraganaIroha"] = "hiragana-iroha";
  ListStyleType["Katakana"] = "katakana";
  ListStyleType["KatakanaIroha"] = "katakana-iroha";
  ListStyleType["LowerAlpha"] = "lower-alpha";
  ListStyleType["LowerGreek"] = "lower-greek";
  ListStyleType["LowerLatin"] = "lower-latin";
  ListStyleType["LowerRoman"] = "lower-roman";
  ListStyleType["None"] = "none";
  ListStyleType["Square"] = "square";
  ListStyleType["UpperAlpha"] = "upper-alpha";
  ListStyleType["UpperLatin"] = "upper-latin";
  ListStyleType["UpperRoman"] = "upper-roman";
  ListStyleType["Initial"] = "initial";
  ListStyleType["Inherit"] = "inherit";
})(ListStyleType || (ListStyleType = {}));

const injectIndentListComponent = props => {
  const {
    element
  } = props;

  if (element[KEY_LIST_STYLE_TYPE]) {
    let className = clsx(`slate-${KEY_LIST_STYLE_TYPE}-${element[KEY_LIST_STYLE_TYPE]}`);
    const style = {
      padding: 0,
      margin: 0,
      listStyleType: element[KEY_LIST_STYLE_TYPE]
    };

    if ([ListStyleType.Disc, ListStyleType.Circle, ListStyleType.Square].includes(element[KEY_LIST_STYLE_TYPE])) {
      className = clsx(className, 'slate-list-bullet');
      return ({
        children
      }) => /*#__PURE__*/React.createElement("ul", {
        style: style,
        className: className
      }, /*#__PURE__*/React.createElement("li", null, children));
    }

    className = clsx(className, 'slate-list-number');
    return ({
      children
    }) => /*#__PURE__*/React.createElement("ol", {
      style: style,
      className: className,
      start: element[KEY_LIST_START]
    }, /*#__PURE__*/React.createElement("li", null, children));
  }
};

const getNextIndentList = (editor, [node, path], {
  sameStyleType = true
} = {}) => {
  const indent = node[KEY_INDENT];
  const listStyleType = node[KEY_LIST_STYLE_TYPE];
  let nextPath = Path.next(path);

  while (true) {
    const nextNode = getNode(editor, nextPath);

    if (!nextNode || !nextNode[KEY_INDENT] || nextNode[KEY_INDENT] < indent) {
      return;
    }

    if (nextNode[KEY_INDENT] === indent) {
      if (sameStyleType && nextNode[KEY_LIST_STYLE_TYPE] !== listStyleType) {
        return;
      }

      return [nextNode, nextPath];
    }

    nextPath = Path.next(nextPath);
  }
};

const getPreviousIndentList = (editor, [node, path], {
  sameStyleType = true
} = {}) => {
  const indent = node[KEY_INDENT];
  const listStyleType = node[KEY_LIST_STYLE_TYPE];
  let prevPath = getPreviousPath(path);

  while (true) {
    if (!prevPath) return;
    const prevNode = getNode(editor, prevPath);

    if (!prevNode || !prevNode[KEY_INDENT] || prevNode[KEY_INDENT] < indent) {
      return;
    }

    if (prevNode[KEY_INDENT] === indent) {
      if (sameStyleType && prevNode[KEY_LIST_STYLE_TYPE] !== listStyleType) {
        return;
      }

      return [prevNode, prevPath];
    }

    prevPath = getPreviousPath(prevPath);
  }
};

const normalizeListStart = (editor, nodeEntry) => {
  const [node, path] = nodeEntry;
  const listStyleType = node[KEY_LIST_STYLE_TYPE];

  if (listStyleType) {
    var _node$KEY_LIST_START, _nextNode$KEY_LIST_ST;

    const prevNodeEntry = getPreviousIndentList(editor, nodeEntry);

    if (!prevNodeEntry && node[KEY_LIST_START] > 1) {
      setNodes(editor, {
        [KEY_LIST_START]: 1
      }, {
        at: path
      });
    }

    const nextNodeEntry = getNextIndentList(editor, nodeEntry);
    if (!nextNodeEntry) return;
    const [nextNode, nextPath] = nextNodeEntry;
    const listStart = (_node$KEY_LIST_START = node[KEY_LIST_START]) !== null && _node$KEY_LIST_START !== void 0 ? _node$KEY_LIST_START : 1;
    const nextListStart = (_nextNode$KEY_LIST_ST = nextNode[KEY_LIST_START]) !== null && _nextNode$KEY_LIST_ST !== void 0 ? _nextNode$KEY_LIST_ST : 1;

    if (nextListStart !== listStart + 1) {
      setNodes(editor, {
        [KEY_LIST_START]: listStart + 1
      }, {
        at: nextPath
      });
      normalizeListStart(editor, [nextNode, nextPath]);
    }
  }
};

const withIndentList = editor => {
  const {
    apply,
    normalizeNode
  } = editor;

  editor.normalizeNode = ([node, path]) => {
    if (node[KEY_LIST_STYLE_TYPE] && !node[KEY_INDENT]) {
      unsetNodes(editor, KEY_LIST_STYLE_TYPE, {
        at: path
      });
    }

    normalizeListStart(editor, [node, path]);
    return normalizeNode([node, path]);
  };

  editor.apply = operation => {
    const {
      path
    } = operation;
    let nodeBefore = null;

    if (operation.type === 'set_node') {
      nodeBefore = getNode(editor, path);
    } // If there is a previous indent list, the inserted indent list style type should be the same.
    // Only for lower-roman and upper-roman as it overlaps with lower-alpha and upper-alpha.


    if (operation.type === 'insert_node') {
      const listStyleType = operation.node[KEY_LIST_STYLE_TYPE];

      if (listStyleType && ['lower-roman', 'upper-roman'].includes(listStyleType)) {
        const prevNodeEntry = getPreviousIndentList(editor, [operation.node, path], {
          sameStyleType: false
        });

        if (prevNodeEntry) {
          const prevListStyleType = prevNodeEntry[0][KEY_LIST_STYLE_TYPE];

          if (prevListStyleType === ListStyleType.LowerAlpha && listStyleType === ListStyleType.LowerRoman) {
            operation.node[KEY_LIST_STYLE_TYPE] = ListStyleType.LowerAlpha;
          } else if (prevListStyleType === ListStyleType.UpperAlpha && listStyleType === ListStyleType.UpperRoman) {
            operation.node[KEY_LIST_STYLE_TYPE] = ListStyleType.UpperAlpha;
          }
        }
      }
    } // FIXME: delete first list
    // let nextNodeEntryBefore: any;
    // if (
    //   operation.type === 'merge_node' &&
    //   operation.properties[KEY_LIST_STYLE_TYPE]
    // ) {
    //   const node = getNode(editor, path);
    //
    //   nextNodeEntryBefore = getNextIndentList(editor, [node as any, path]);
    // }


    apply(operation);

    if (operation.type === 'merge_node') {
      const {
        properties
      } = operation;

      if (properties[KEY_LIST_STYLE_TYPE]) {
        const node = getNode(editor, path);
        if (!node) return;
        const prevNodeEntry = getPreviousIndentList(editor, [node, path]);

        if (!prevNodeEntry) {
          normalizeListStart(editor, [node, path]);
          return;
        }

        normalizeListStart(editor, prevNodeEntry); // FIXME: delete first list
        // if (nextNodeEntryBefore) {
        //   normalizeListStart(editor,nextNodeEntryBefore);
        // }
      }
    }

    if (nodeBefore) {
      if (operation.type === 'set_node') {
        const prevListStyleType = operation.properties[KEY_LIST_STYLE_TYPE];
        const listStyleType = operation.newProperties[KEY_LIST_STYLE_TYPE]; // Remove list style type

        if (prevListStyleType && !listStyleType) {
          const node = getNode(editor, path);
          if (!node) return;
          const nextNodeEntry = getNextIndentList(editor, [node, path]);
          if (!nextNodeEntry) return;
          normalizeListStart(editor, nextNodeEntry);
        } // Update list style type


        if ((prevListStyleType || listStyleType) && prevListStyleType !== listStyleType) {
          const node = getNode(editor, path);
          if (!node) return;
          /**
           * Case:
           * - 1-<o>-1 <- toggle ol
           * - <1>-1-2 <- normalize
           * - 1-2-3
           */

          const prevNodeEntry = getPreviousIndentList(editor, [node, path]);

          if (prevNodeEntry) {
            normalizeListStart(editor, prevNodeEntry);
          }
          /**
           * Case:
           * - 1-<2>-3 <- toggle ul
           * - 1-o-<3> <- normalize
           * - 1-o-1
           */


          const nextNodeEntry = getNextIndentList(editor, [nodeBefore, path]);

          if (nextNodeEntry) {
            normalizeListStart(editor, nextNodeEntry);
          }
        }

        const prevIndent = operation.properties[KEY_INDENT];
        const indent = operation.newProperties[KEY_INDENT]; // Update indent

        if (prevIndent !== indent) {
          const node = getNode(editor, path);
          if (!node) return;
          /**
           * Case:
           * - 1-<o>-1 <- indent
           * - <1>-1o-1 <- normalize node before
           * - 1-1o-2
           */

          let prevNodeEntry = getPreviousIndentList(editor, [nodeBefore, path], {
            sameStyleType: false
          });

          if (prevNodeEntry) {
            normalizeListStart(editor, prevNodeEntry);
          }
          /**
           * Case:
           * - 11-<1>-11 <- indent
           * - <11>-11-12 <- normalize prev node after
           * - 11-12-13
           */


          prevNodeEntry = getPreviousIndentList(editor, [node, path], {
            sameStyleType: false
          });

          if (prevNodeEntry) {
            normalizeListStart(editor, prevNodeEntry);
          }
          /**
           * Case:
           * - 11-<12>-13 <- outdent
           * - 11-2-<13> <- normalize next node before
           * - 11-2-11
           */


          let nextNodeEntry = getNextIndentList(editor, [nodeBefore, path], {
            sameStyleType: false
          });

          if (nextNodeEntry) {
            normalizeListStart(editor, nextNodeEntry);
          }
          /**
           * Case:
           * - 1-<1o>-2 <- outdent
           * - 1-o-<2> <- normalize next node after
           * - 1-o-1
           */


          nextNodeEntry = getNextIndentList(editor, [node, path], {
            sameStyleType: false
          });

          if (nextNodeEntry) {
            normalizeListStart(editor, nextNodeEntry);
          }
        }
      }
    }
  };

  return editor;
};

const KEY_LIST_STYLE_TYPE = 'listStyleType';
const KEY_LIST_START = 'listStart';
const createIndentListPlugin = createPluginFactory({
  key: KEY_LIST_STYLE_TYPE,
  inject: {
    belowComponent: injectIndentListComponent
  },
  withOverrides: withIndentList
});

/**
 * Increase the indentation of the selected blocks.
 */
const indentList = (editor, {
  listStyleType = ListStyleType.Disc,
  ...options
} = {}) => {
  setIndent(editor, {
    offset: 1,
    setNodesProps: () => ({
      [KEY_LIST_STYLE_TYPE]: listStyleType
    }),
    ...options
  });
};

/**
 * Decrease the indentation of the selected blocks.
 */
const outdentList = (editor, options = {}) => {
  setIndent(editor, {
    offset: -1,
    unsetNodesProps: [KEY_LIST_STYLE_TYPE],
    ...options
  });
};

/**
 * Increase the indentation of the selected blocks.
 */

const toggleIndentList = (editor, options) => {
  const nodeEntry = findNode(editor, {
    match: n => !!n[KEY_LIST_STYLE_TYPE]
  });

  if (!nodeEntry) {
    indentList(editor, options);
    return;
  }

  const [node, path] = nodeEntry;

  if (node[KEY_LIST_STYLE_TYPE] && options !== null && options !== void 0 && options.listStyleType) {
    if (node[KEY_LIST_STYLE_TYPE] === (options === null || options === void 0 ? void 0 : options.listStyleType)) {
      outdentList(editor, options);
      unsetNodes(editor, KEY_LIST_STYLE_TYPE);
      return;
    }

    setNodes(editor, {
      [KEY_LIST_STYLE_TYPE]: options === null || options === void 0 ? void 0 : options.listStyleType
    }, {
      at: path
    });
  }
};

export { KEY_LIST_START, KEY_LIST_STYLE_TYPE, ListStyleType, createIndentListPlugin, getNextIndentList, getPreviousIndentList, indentList, injectIndentListComponent, normalizeListStart, outdentList, toggleIndentList, withIndentList };
//# sourceMappingURL=index.es.js.map
